import Vue from 'vue';
import VueI18nManager from 'vue-i18n-manager';
import store from '@/store';
import router from '@/router';
import {translation} from '@/helpers/translation'
import {isAbortedRequest} from '@/utils/https'


const loadTranslations = async (locale, attempt = 0) => {
    const normalizedLocale = locale || store.getters.currentLanguage?.translationKey || 'uk';

    try {
        return await store.dispatch('setting/GET_TRANSLATIONS', normalizedLocale);
    } catch (error) {
        const cached = store.state.setting?.translationsByLocale?.[normalizedLocale]
            || store.state.setting?.translations;

        if (cached) {
            return cached;
        }

        // Retry transient failures — an aborted request or, importantly, a 429
        // rate-limit / 5xx on prod — with exponential backoff before giving up.
        // Without this the first visit that hits a 429 renders raw i18n keys.
        const status = error && error.status ? error.status : null;
        const retriable = isAbortedRequest(error) || status === 429 || status === 502 || status === 503 || status === 504;

        if (retriable && attempt < 3) {
            const delay = 400 * Math.pow(2, attempt); // 400, 800, 1600ms
            await new Promise((resolve) => setTimeout(resolve, delay));
            return loadTranslations(normalizedLocale, attempt + 1);
        }

        throw error;
    }
};

const proxy = {
    getTranslation: async (lang) => {
        const response = await loadTranslations(lang?.translationKey);
        Vue.use(response, translation);
        return response;
    }
};


Vue.use(VueI18nManager, {
    store,
    router,
    proxy,
    config: {
        defaultCode: 'ua-UA',
        languages: [
            {
                name: 'Ukranian',
                title: 'Ua.',
                key: 'ua',
                code: 'ua-UA',
                translationKey: 'uk',
                urlPrefix: '',
            },
            {
                name: 'Russian',
                title: 'Rus.',
                key: 'ru',
                code: 'ru-RU',
                urlPrefix: 'ru',
                translationKey: 'ru',
            },
        ],
        translations: proxy,
    },
});

export function invalidateLocaleTranslations(storeInstance, locale) {
    if (!locale || !storeInstance?.state) {
        return;
    }

    if (storeInstance.state.setting?.translationsByLocale?.[locale]) {
        Vue.delete(storeInstance.state.setting.translationsByLocale, locale);
    }

    const i18nState = storeInstance.state['vue-i18n-manager'];
    if (i18nState?.translations?.[locale]) {
        Vue.delete(i18nState.translations, locale);
    }
}

export function resolveLocaleFromPath(pathname = window.location.pathname) {
    return pathname.split('/').filter(Boolean)[0] === 'ru' ? 'ru' : 'uk';
}

export async function syncLocaleFromUrl(storeInstance) {
    const translationKey = resolveLocaleFromPath();
    const language = storeInstance.getters.languages.find(
        (item) => item.translationKey === translationKey,
    );

    if (!language) {
        return;
    }

    invalidateLocaleTranslations(storeInstance, translationKey);

    await storeInstance.dispatch('vue-i18n-manager/SET_LANGUAGE', language.code);
}

export function patchLocalizedRouting(routerInstance, storeInstance) {
    if (Array.isArray(routerInstance.afterHooks)) {
        routerInstance.afterHooks.length = 0;
    }

    if (Array.isArray(routerInstance.beforeHooks)) {
        routerInstance.beforeHooks.length = 0;
    }

    routerInstance.beforeEach((to, from, next) => {
        const {availableLanguages, currentLanguage, forceTranslation, languages} = storeInstance.getters;
        const languageList = forceTranslation ? languages : availableLanguages;
        const routeLang = to.params.lang || '';
        const urlLanguage = languageList.find((language) => language.urlPrefix === routeLang);

        if (urlLanguage && urlLanguage.urlPrefix !== currentLanguage.urlPrefix) {
            invalidateLocaleTranslations(storeInstance, urlLanguage.translationKey);

            return storeInstance.dispatch('vue-i18n-manager/SET_LANGUAGE', urlLanguage.code).then(() => next());
        }

        return next();
    });

    const resolveLangPrefix = () => {
        const routeLang = routerInstance.currentRoute?.params?.lang;

        if (routeLang) {
            return routeLang;
        }

        return storeInstance.getters.currentLanguage?.urlPrefix || '';
    };

    const normalizeRouteLocation = (location) => {
        if (!location || typeof location !== 'object' || !location.params) {
            return location;
        }

        const params = {...location.params};

        if (!params.lang) {
            delete params.lang;
        }

        return {
            ...location,
            params,
        };
    };

    Vue.prototype.$localize = (route) => {
        const prefix = resolveLangPrefix();
        const params = {...(route.params || {})};

        if (prefix) {
            params.lang = prefix;
        } else {
            delete params.lang;
        }

        return {
            ...route,
            params,
        };
    };

    ['push', 'replace'].forEach((method) => {
        const original = routerInstance[method].bind(routerInstance);

        routerInstance[method] = (location, onResolve, onReject) => {
            const normalized = typeof location === 'object'
                ? normalizeRouteLocation(location)
                : location;

            return original(normalized, onResolve, onReject);
        };
    });
}
